import { useState, useEffect, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import "./waiver.css";
import { useStudio } from "../../studios/StudioContext";

export default function WaiverPage() {
  const studio = useStudio();
  const API = studio.apiBaseUrl;
  const params     = new URLSearchParams(window.location.search);
  const emailParam = params.get("email") ?? "";
  const nameParam  = params.get("name")  ?? "";

  const [name, setName]                   = useState(nameParam);
  const [email, setEmail]                 = useState(emailParam);
  const [rawSections, setRawSections]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [submitting, setSubmitting]       = useState(false);
  const [submitted, setSubmitted]         = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [error, setError]                 = useState("");
  const [sigEmpty, setSigEmpty]           = useState(true);
  const [blurredEmail, setBlurredEmail]   = useState("");

  const sigRef = useRef(null);

  // Fetch waiver text ONCE on mount with empty name
  useEffect(() => {
    fetch(`${API}/waiver/text?name=`)
      .then(r => r.json())
      .then(data => { setRawSections(data.sections ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [API]);

  // Substitute name live in the browser as the student types
  const displaySections = rawSections.map(s => ({
    ...s,
    body: (s.body || "").replaceAll(
      "[STUDENT_NAME]",
  name.trim() || "[STUDENT_NAME]"
),
  }));

  // Check if already signed when email is known
 useEffect(() => {
  if (!blurredEmail) return;
  fetch(`${API}/waiver/status?email=${encodeURIComponent(blurredEmail)}`)
    .then(r => r.json())
   .then(data => {
     if (data.studentName && !name) setName(data.studentName);
     if (data.signed) setAlreadySigned(true);
   })
    .catch(err => console.error("WaiverPage: waiver status prefill failed", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [blurredEmail]);

  const handleClear = () => { sigRef.current?.clear(); setSigEmpty(true); };

  const handleSubmit = async () => {
    if (!name.trim())  { setError("Please enter your full name."); return; }
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (!email.includes("@")) { setError("Please enter a valid email address."); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError("Please sign the waiver before submitting."); return;
    }
    setSubmitting(true);
    setError("");
    try {
      const signature = sigRef.current.toDataURL("image/png");
      const res  = await fetch(`${API}/waiver/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase(), signature }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Something went wrong."); return; }
      setSubmitted(true);
    } catch {
      setError("Network error — please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div className="waiver-wrap">
      <div className="waiver-success">
        <div className="waiver-success-icon">✓</div>
        <h2 className="waiver-success-title">Waiver signed</h2>
        <p className="waiver-success-sub">Thank you, {name.split(" ")[0]}. A copy of your signed waiver has been emailed to {email}.</p>
        <p className="waiver-success-note">Your waiver remains on file for the duration of your enrollment. We'll let you know if we ever need a fresh signature.</p>
      </div>
    </div>
  );

  if (alreadySigned) return (
    <div className="waiver-wrap">
      <div className="waiver-success">
        <div className="waiver-success-icon">✓</div>
        <h2 className="waiver-success-title">Already signed</h2>
        <p className="waiver-success-sub">{name || "You"} — we already have your signed waiver on file. You're all set.</p>
      </div>
    </div>
  );

  return (
    <div className="waiver-wrap">
      <div className="waiver-inner">
        <div className="waiver-header">
          <div className="waiver-studio">{studio?.studioName}</div>
          <h1 className="waiver-title">Liability Waiver & Release</h1>
          <p className="waiver-subtitle">Please read carefully, enter your details, and sign below.</p>
        </div>

        <div className="waiver-fields">
          <div className="waiver-field-group">
            <label className="waiver-label">Full name</label>
            <input className="waiver-input" type="text" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="waiver-field-group">
            <label className="waiver-label">Email address</label>
            <input className="waiver-input" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => setBlurredEmail(email)}  />
          </div>
        </div>

        <div className="waiver-document">
          {loading
            ? <p className="waiver-loading">Loading waiver…</p>
            : displaySections.map((section, i) => (
                <div key={i} className="waiver-section">
                  {section.heading && <h3 className="waiver-section-heading">{section.heading}</h3>}
                  <p className="waiver-section-body">{section.body}</p>
                </div>
              ))
          }
        </div>

        <div className="waiver-sig-block">
          <div className="waiver-sig-header">
            <label className="waiver-label">Signature</label>
            <button className="waiver-clear-btn" onClick={handleClear} type="button">Clear</button>
          </div>
          <div className="waiver-sig-canvas-wrap">
            <SignatureCanvas ref={sigRef} penColor="#1a0a12" canvasProps={{ className: "waiver-sig-canvas" }} onEnd={() => setSigEmpty(false)} />
            {sigEmpty && <div className="waiver-sig-placeholder">Sign here with your finger or mouse</div>}
          </div>
          <p className="waiver-sig-note">By signing above, you confirm that you have read, understood, and agree to the terms of this liability waiver and release.</p>
        </div>

        {error && <p className="waiver-error">{error}</p>}

        <button className="waiver-submit-btn" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting…" : "Sign & Submit Waiver"}
        </button>

        <p className="waiver-legal">This electronic signature is legally binding under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act). Your signed copy will be emailed to you immediately upon submission.</p>
      </div>
    </div>
  );
}
